<template>
  <v-dialog
      fullscreen
      eager
      v-model="dialog"
      hide-overlay
      content-class="form-dialog"
  >
    <v-card elevation="0">
      <v-card-text class="pa-0">
        <v-form ref="form">
          <v-row no-gutters justify="space-around">
            <v-col cols="12">
              <v-row justify="space-around">
                <v-col cols="12" class="text-right pr-5">
                  <v-btn icon @click="closeDialog" ><v-icon large class="mx-5" color="primary">close</v-icon></v-btn>
                </v-col>
                <v-col cols="12" md="4">
                  <!-- General Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('common:general') }}</h4>
                  <!-- Select process -->
                  <v-select
                      outlined
                      v-model="processResult.process"
                      :label="$t('common:process')"
                      :menu-props="{ bottom: true, offsetY: true }"
                      :rules="[ruleRequired($t('utils:rules.required'))]"
                      :items="restProcess"
                      item-text="name"
                      item-value="id"
                      persistent-hint
                      @change="getProcessSteps"
                      tabindex="55"
                  />

                  <!-- Input User -->
                  <v-text-field
                      outlined
                      v-model="processResult.username"
                      :label="$t('book/facility:facility.manual-process-dialog.labels.done-by-user')"
                      :rules="[ruleRequired($t('utils:rules.required'))]"
                      clearable
                      tabindex="56"
                  />

                  <DateField
                      ref="doneOnDate"
                      @update="(v) => (processResult.doneOnDate = v)"
                      :isClearable="true"
                      :isRequired="true"
                      :label="$t('book/facility:facility.manual-process-dialog.labels.done-on-date')"
                      :onlyPast="true"
                      tabindex="57"
                  />

                  <!-- Time picker -->
                  <v-menu
                      ref="menuDoneOnTime"
                      v-model="menuDoneOnTime"
                      :close-on-content-click="false"
                      :return-value.sync="processResult.doneOnTime"
                      transition="scale-transition"
                      offset-x
                      min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                          v-model="processResult.doneOnTime"
                          outlined
                          :label="$t('book/facility:facility.manual-process-dialog.labels.done-on-time')"
                          :rules="[ruleRequired($t('utils:rules.required'))]"
                          persistent-hint
                          prepend-inner-icon="schedule"
                          readonly
                          v-on="on"
                          v-bind="attrs"
                          class="mb-3"
                          tabindex="58"
                      />
                    </template>
                    <v-time-picker
                        v-if="menuDoneOnTime"
                        format="24hr"
                        v-model="processResult.doneOnTime"
                        scrollable
                        @click:minute="$refs.menuDoneOnTime.save(processResult.doneOnTime)"
                    />
                  </v-menu>

                  <v-text-field
                      outlined
                      v-model="processResult.rideCount"
                      :label="$t('book/facility:facility.manual-process-dialog.labels.ride-count')"
                      clearable
                      type="number"
                      tabindex="59"
                  />

                  <BaseButton
                      color="primary"
                      :btnText="$t('book/facility:facility.manual-process-dialog.save-btn')"
                      @clickedButton="postForm()"
                  />

                  <DirtyConfirmButtonDialog
                      ref="dirtyDialog"
                      :btn-text="$t('buttons:cancel')"
                      color="grey"
                      :dialog-title="$t('dialog:dirty-save-btn-title')"
                      :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                      :button1-text="$t('dialog:dirty-save-btn-1-text')"
                      :button2-text="$t('dialog:dirty-save-btn-2-text')"
                      @clickedButton="closeDialog"
                      :dataToWatch="processResult"
                      hideOverlay
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <!-- Table Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('book/facility:facility.manual-process-dialog.titles.table') }}</h4>
                  <v-simple-table class="elevation-1">
                    <template v-slot:default>
                      <thead>
                      <tr>
                        <th class="text-left">
                          {{ $t("common:process-step") }}
                        </th>
                        <th class="text-left">
                          {{ $t("common:success") }}
                        </th>
                        <th class="text-left">
                          {{ $t("common:answer_plural") }}
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr
                          v-for="step in restProcessSteps"
                          :key="step.id"
                      >
                        <td>{{ step.name }}</td>
                        <td>
                          <v-checkbox
                              color="secondary"
                              v-model="step.selected"
                              @change="checkSuccess"
                          />
                        </td>
                        <td>
                          <v-select
                              v-if="!step.selected"
                              outlined
                              v-model="step.selectedAnswer"
                              :label="$t('common:answer')"
                              :menu-props="{ bottom: true, offsetY: true }"
                              :items="step.answers"
                              dense
                              hide-details

                          />
                        </td>
                      </tr>
                      </tbody>
                    </template>
                  </v-simple-table>

                  <LabelField
                      :label="$t('book/facility:facility.manual-process-dialog.labels.state')"
                      :value="processSuccess"
                      :small="true"
                      class="mt-5"
                  />
                </v-col>
              </v-row>
            </v-col>

            <!-- TODO add ProcessDocuSidebar when uploading docu at adding manual process result is implemented -->
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>

import ProcessService from "@/modules/book/services/process/impl/process-service";
import ProcessResultService from "@/modules/book/services/process/impl/process-result-service";

import DataUtils from "@/modules/core/services/data-utils";
import Utils from "@/modules/core/services/utils";
import {ruleRequired} from "@/modules/core/services/rules";
import parse from "date-fns/parseISO";
import utcToZonedTime from "date-fns-tz/utcToZonedTime"
import format from 'date-fns/format';

import ProcessResult from '@/modules/book/data/processResult';
import ProcessStepResult from '@/modules/book/data/processStepResult';
import ProcessStep from '@/modules/book/data/processStep';
import Process from '@/modules/book/data/process';

import DateField from '@/modules/core/components/DateField';
import LabelField from '@/modules/core/components/LabelField'
import BaseButton from "@/modules/core/components/Base/BaseButton";
import DirtyConfirmButtonDialog from "@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog";
import DateUtils from "@/modules/core/services/date-utils";

export default {
  name: 'FacilityManualProcessDialogForm',
  components: {
    DateField,
    LabelField,
    BaseButton,
    DirtyConfirmButtonDialog
  },
  data() {
    return {
      valid: false,

      processResult: new ProcessResult(),

      restProcess: [],
      restProcessSteps: [],
      listOptions: {
        options: [],
        search: [],
        filter: null
      },

      menuDoneOnTime: false,

      processSuccess: '',
      dialog: false
    }
  },
  methods: {
    ruleRequired,
    openDialog(options, search, filter) {
      this.listOptions.options = options;
      this.listOptions.search = search;
      this.listOptions.filter = filter;

      this.processResult = new ProcessResult();

      ProcessService.readAllManualFromAPI(
        (resp) => {
          this.restProcess = DataUtils.parseDataToObjectArray(resp.data, Process);
        },
        (error) => {
          this.$logger.error("Error at ProcessService.readAllManualFromAPI: " + error)
          Utils.showFailureSnackbar(this.$t('book/facility:facility.manual-process-dialog.snackbar.load-process-fail'))
        }
      )
      this.$refs.dirtyDialog.startWatch();
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.$refs.form.reset();
      this.processResult = new ProcessResult();
    },

    checkSuccess() {
      for (var i = 0; i < this.restProcessSteps.length; i++) {
        if (!this.restProcessSteps[i].selected) {
          this.processSuccess = this.$t('book/facility:facility.manual-process-dialog.no-success')
          return
        }
      }
      this.processSuccess = this.$t('common:success');
    },

    getProcessSteps(item) {
      if(this.dialog) {
        ProcessService.readProcessStepsFromAPI(item,
        (resp) => {
          this.restProcessSteps = DataUtils.parseDataToObjectArray(resp.data, ProcessStep);
          this.processSuccess = this.$t('book/facility:facility.manual-process-dialog.no-success');
        })
      }
    },

    postForm() {
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }

      let counter = 0;
      // transform to ProcessStepResult Object
      for (var i = 0; i < this.restProcessSteps.length; i++) {
        let psr = new ProcessStepResult();
        psr.step = this.restProcessSteps[i].id;
        psr.name = this.restProcessSteps[i].name;

        if(!this.restProcessSteps[i].selected) {
          psr.answer = this.restProcessSteps[i].selectedAnswer;
        }
        psr.success = this.restProcessSteps[i].selected;
        psr.finished = true;

        this.processResult.stepResults.push(psr);

        if (psr.success) {
          counter++;
        }
      }

      // transfer udateable property from process to result
      for (var j = 0; j < this.restProcessSteps.length; j++) {
        if (this.restProcessSteps[j].id == this.processSuccess.process) {
          this.ProcessResult.updateable = this.restProcessSteps[j].updateable;
        }
      }

      //construct date, and set from user local timezone to UTC
      let date = parse(DateUtils.parseDateForVuetify(this.processResult.doneOnDate) + " " + this.processResult.doneOnTime);
      date = utcToZonedTime(date, 'UTC');
      this.processResult.finishedOn = format(date,process.env.VUE_APP_DATETIME_FORMAT_DTO);

      this.processResult.facility = this.$route.params.id;
      this.processResult.manuallyAdded = true;

      // if every process step result is successfull, make whole result successfull
      if (this.restProcessSteps.length === counter) {
        this.processResult.success = true;
      }

      ProcessResultService.postToAPI(this.processResult,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('book/facility:facility.manual-process-dialog.snackbar.result')}));
          this.$emit('updateProcessResults', this.listOptions);
          this.closeDialog();
        },
        () => {
          Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('book/facility:facility.manual-process-dialog.snackbar.result')}))
        })
    }
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    },
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.$refs.form.resetValidation()
      });
    }
  }
}
</script>